import { BadgePercent, Gift, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import { formatIvaRate } from '@/shared/countries';
import { MoneyCell, type Moneda } from './MoneyCell';
import { ddmmyyyy, GarantiaBadge, type GarantiaLinea } from './Garantia';
import type { InvoiceTotals, LineTotals } from '../lib/calc';

export type DetalleLineaRow = {
  detalleId: string;
  articulo: string;
  monto: number;
  cantidad: number;
  descuento: number;
  /** IVA rate as a percentage (e.g. 21, 10.5, 16, 8, 0). 0 = Exento. */
  iva: number;
  esRegalo: boolean;
  campaignPct: number | null;
  needsAuthorization: boolean;
  /** yyyy-mm-dd. Ingreso estimado de stock por ingresar (informativo). */
  fechaEstimadaIngreso?: string | null;
  totals: LineTotals;
} & GarantiaLinea;

/**
 * Read-only modal that shows every line of the cotización plus its
 * computed totals. Opened from the "Ver detalle" action on the Cotización
 * stage card — it's the equivalent of the HTML-rendered quote preview in
 * PowerApps' `Screen_DetalleVenta`, except we render it natively so the
 * data stays selectable/accessible.
 */
export function DetalleLineasDialog({
  open,
  onClose,
  lines,
  invoice,
  moneda,
  title,
  loading = false,
}: {
  open: boolean;
  onClose: () => void;
  lines: DetalleLineaRow[];
  invoice: InvoiceTotals;
  moneda: Moneda;
  title: string;
  /** True while the lines query is still in flight (or refetching). */
  loading?: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {loading && lines.length === 0 ? (
            <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed bg-muted/30 p-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando detalles…
            </div>
          ) : lines.length === 0 ? (
            <div className="rounded-xl border border-dashed bg-muted/30 p-6 text-center text-sm text-muted-foreground">
              Esta cotización no tiene detalles cargados.
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Artículo</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Monto</th>
                    <th className="w-16 px-3 py-2 text-right font-semibold">Cant.</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Subtotal</th>
                    <th className="w-14 px-3 py-2 font-semibold">IVA</th>
                    <th className="w-20 px-3 py-2 text-right font-semibold">Desc.</th>
                    <th className="w-28 px-3 py-2 text-right font-semibold">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {lines.map((m) => (
                    <tr key={m.detalleId}>
                      <td className="px-3 py-2 align-middle">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-foreground">{m.articulo}</span>
                          {m.esRegalo && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-violet-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-violet-700">
                              <Gift className="h-3 w-3" aria-hidden /> Regalo
                            </span>
                          )}
                          <GarantiaBadge linea={m} />
                          {m.campaignPct != null && !m.esRegalo && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                              <BadgePercent className="h-3 w-3" aria-hidden /> Campaña {m.campaignPct}%
                            </span>
                          )}
                          {m.needsAuthorization && (
                            <span className="inline-flex items-center gap-1 rounded-md bg-fuchsia-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-fuchsia-700">
                              Aprobación Requerida
                            </span>
                          )}
                        </div>
                        {m.fechaEstimadaIngreso && (
                          <p className="mt-0.5 text-xs text-sky-700">
                            Ingreso estimado: {ddmmyyyy(m.fechaEstimadaIngreso)}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right align-middle">
                        <MoneyCell amount={m.monto} moneda={moneda} />
                      </td>
                      <td className="px-3 py-2 text-right align-middle text-foreground">
                        {m.cantidad}
                      </td>
                      <td className="px-3 py-2 text-right align-middle">
                        <MoneyCell amount={m.totals.subtotal} moneda={moneda} />
                      </td>
                      <td className="px-3 py-2 align-middle text-muted-foreground">
                        {m.iva === 0 ? '—' : `${String(m.iva).replace('.', ',')}%`}
                      </td>
                      <td className="px-3 py-2 text-right align-middle text-foreground">
                        {m.descuento > 0 ? `${m.descuento}%` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right align-middle">
                        <MoneyCell amount={m.totals.total} moneda={moneda} emphasise />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <TotalPill label="Subtotal" value={invoice.subtotalDescontado} moneda={moneda} />
            {Object.entries(invoice.ivaByRate)
              .map(([k, v]) => [Number(k), v] as const)
              .sort((a, b) => b[0] - a[0])
              .map(([rate, amount]) => (
                <TotalPill
                  key={rate}
                  label={`IVA ${formatIvaRate(rate)}%`}
                  value={amount}
                  moneda={moneda}
                />
              ))}
            <TotalPill label="Total" value={invoice.total} moneda={moneda} emphasise />
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

function TotalPill({
  label,
  value,
  moneda,
  emphasise,
}: {
  label: string;
  value: number;
  moneda: Moneda;
  emphasise?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-11 flex-col justify-center rounded-xl border bg-secondary px-4 leading-tight',
        emphasise && 'border-primary/30 bg-primary/5',
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <MoneyCell amount={value} moneda={moneda} emphasise={emphasise} />
    </div>
  );
}
